import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, firstValueFrom, fromEvent, map, merge } from 'rxjs';
import { SubSink } from 'subsink';

import { TawkToSecretGQL } from '../../generated/graphql.generated';
import { PlusAuthenticationService } from '../core/public-api';

import { MS_ONE_MINUTE } from '../time';

import { BrandingService } from './branding.service';
import { DatadogService } from './datadog.service';

@Injectable({
  providedIn: 'root'
})
export class TawkToService {

  private static readonly EMBED_SRC = 'https://embed.tawk.to/6792663d825083258e0a0b36/1ii9tuom2';
  private static readonly LOAD_TIMEOUT_MS = 10_000;
  private static readonly MAX_LOAD_RETRIES = 3;

  loaded = new BehaviorSubject(false);
  loadFailed = new BehaviorSubject(false);
  secret: string;
  datadogLastSent?: number;
  datadogSendEventInterval = MS_ONE_MINUTE;

  private loadRetryCount = 0;
  private loadTimeoutHandle?: ReturnType<typeof setTimeout>;

  get Tawk_API() {
    return (window as any).Tawk_API as {
      // start(): void;
      login(data: {
        hash?: string;
        userId?: string;
        email?: string;
        phone?: string;
        zone?: string;
        name?: {
          first?: string;
          last?: string;
        }

        datadog?: string;
      }, callback?: (error?) => void): void;
      logout?(callback?: (error?) => void): void;

      addEvent(eventName: string, metadata: any, callback?: (err) => void);
      setAttributes(attributes: any, callback?: (err) => void);
    };
  }

  subs = new SubSink();

  constructor(
    private brandingSvc: BrandingService,
    private authSvc: PlusAuthenticationService,
    private tawkToSecretGQL: TawkToSecretGQL,
    private datadogSvc: DatadogService,
  ) { }

  init() {
    this.subs.unsubscribe();

    // TODO: on chat started add datadog information

    // on store updated update login information

    this.loadRetryCount = 0;
    this.loadFailed.next(false);

    this.subs.sink = fromEvent(window, 'tawkLoad').subscribe(() => this.handleLoad());
    this.subs.sink = merge(
      fromEvent(window, 'tawkPrechatSubmit'),
      fromEvent(window, 'tawkChatMessageVisitor'),
    ).subscribe(() => this.sendDatadogEvent());

    if ((window as any).Tawk_Loaded) {
      this.handleLoad();
    } else {
      this.armLoadTimeout();
    }

    this.subs.sink = this.authSvc.authState.pipe(
      distinctUntilChanged(),
    ).subscribe((res) => {
      if (res === 'authenticated') {
        this.login();
      } else if (res === 'deauthenticated') {
        // reinjectEmbed() briefly replaces window.Tawk_API with a bare stub
        // while a retry's new script loads; guard against that window lacking
        // logout() so a deauth transition can't kill this subscription.
        this.Tawk_API.logout?.();
      }

    });

  }

  // If the widget loads after being marked failed, restart authentication
  // rather than leaving an earlier login() that already bailed out on the
  // failure signal permanently unauthenticated.
  private handleLoad() {
    clearTimeout(this.loadTimeoutHandle);
    this.loaded.next(true);

    if (this.loadFailed.value) {
      console.warn('[Tawk.To] Widget loaded after being marked failed, restarting authentication');
      this.loadFailed.next(false);
      if (this.authSvc.authState.value === 'authenticated') {
        this.login();
      }
    }
  }

  // The embed script's own onLoad only fires once tawk.to's backend accepts its
  // internal session/start call; that call intermittently 504s. Retry the whole
  // embed (a fresh script tag triggers a fresh session/start) rather than hanging
  // on a load that may never come.
  private armLoadTimeout() {
    clearTimeout(this.loadTimeoutHandle);
    this.loadTimeoutHandle = setTimeout(() => this.retryLoad(), TawkToService.LOAD_TIMEOUT_MS);
  }

  private retryLoad() {
    if (this.loaded.value) { return; }

    if (this.loadRetryCount >= TawkToService.MAX_LOAD_RETRIES) {
      console.error('[Tawk.To] Widget failed to load after', TawkToService.MAX_LOAD_RETRIES, 'retries');
      this.loadFailed.next(true);
      return;
    }

    this.loadRetryCount++;
    console.warn(`[Tawk.To] Widget did not load within ${ TawkToService.LOAD_TIMEOUT_MS }ms, retrying)`);

    this.reinjectEmbed();
    this.armLoadTimeout();
  }

  private reinjectEmbed() {
    document.querySelectorAll('script[src^="' + TawkToService.EMBED_SRC + '"]').forEach((el) => el.remove());
    document.querySelectorAll(
      // eslint-disable-next-line max-len
      '[id^="tawk-"], [class^="tawk-"], iframe[title="chat widget"], iframe[name^="tawk"], #tawkchat-container, .tawk-min-container, .tawk-button-container'
    ).forEach((el) => el.remove());

    (window as any).Tawk_API = {};
    (window as any).Tawk_LoadStart = new Date();

    const s1 = document.createElement('script');
    const s0 = document.getElementsByTagName('script')[0];
    s1.async = true;
    s1.src = `${ TawkToService.EMBED_SRC }?retry=${ this.loadRetryCount }`;
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s0.parentNode.insertBefore(s1, s0);
  }

  async login() {
    const authorization = await this.getAuthorization();

    // wait for loaded, but give up once the widget has failed to load after retries
    const didLoad = await firstValueFrom(combineLatest([this.loaded, this.loadFailed]).pipe(
      filter(([loaded, failed]) => loaded || failed),
      map(([loaded]) => loaded),
    ));

    if (!didLoad) {
      console.warn('[Tawk.To] Skipping login, widget never loaded');
      return;
    }

    console.log(`[Tawk.To] Authenticating...`);

    if (!this.Tawk_API) { return; }
    if (!this.loaded) { return; }
    if (!authorization?.hash) { return; }
    // authState may have moved on (e.g. deauthenticated) while the awaits above
    // were in flight; don't run login logic for a session that already logged out
    if (this.authSvc.authState.value !== 'authenticated') { return; }
    // not supported when not in secure mode
    // this.Tawk_API.login({
    //   ...authorization,
    // });

    // only supported in insecure mode
    this.Tawk_API.setAttributes({
      ...authorization,
      ...this.getAttributes(),
    }, (err) => {
      if (err) {
        console.error('[Tawk.To] Failed to set attributes:', err);
      }
    });

  }

  getAttributes() {
    const attributes: any = {
      email: this.authSvc.user?.email,
      phone: this.authSvc.user?.phone,
      name: `${ this.authSvc.user?.givenName } ${ this.authSvc.user?.familyName }`,
      zone: this.authSvc.contextedZoneId,
    };

    const datadog = this.datadogSvc.getSessionURL();
    if (datadog) {
      attributes.datadog = datadog;
    }

    return attributes;
  }

  async sendDatadogEvent() {
    const sessionUrl = this.datadogSvc.getSessionURL();
    if (!sessionUrl) { return; }

    const attributes = {
      ...await this.getAuthorization(),
      datadog: sessionUrl,
    };

    this.Tawk_API.setAttributes(attributes, (err) => {
      if (err) {
        console.error('[Tawk.To] Datadog attribute sync failed:', err);
      }
    });

    if (this.datadogLastSent < this.datadogSendEventInterval + Date.now()) {
      return;
    }

    this.datadogLastSent = Date.now();

    // doesn work
    this.Tawk_API.addEvent('datadog', {
      url: sessionUrl,
    }, (err) => {
      if (err) {
        console.error('[Tawk.To] Datadog event failed:', err);
      }
    });

  }

  async getAuthorization() {
    if (!this.secret && this.authSvc.user) {
      const { data: { tawkToSecret } } = await firstValueFrom(this.tawkToSecretGQL.fetch());
      this.secret = tawkToSecret;
    }

    if (!this.secret) {
      return {};
    }

    return {
      hash: this.secret,
      userId: this.authSvc.user?.id,
    }
  }
}
